import { useId, useRef, useSyncExternalStore } from "react";
import { usePlatformLocale } from "../lib/platform-language";
import { readLocale, type Locale } from "../lib/i18n";
import { PlatformDialogSurface } from "./PlatformDialogSurface";

export type PlatformConfirmOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  busyLabel?: string;
  danger?: boolean;
  locale?: Locale;
};

type PlatformConfirmRequest = PlatformConfirmOptions & {
  id: number;
  resolve: (confirmed: boolean) => void;
};

let nextRequestId = 1;
let queue: PlatformConfirmRequest[] = [];
const listeners = new Set<() => void>();

function emitChange() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return queue[0] ?? null;
}

export function confirmAction(options: PlatformConfirmOptions | string): Promise<boolean> {
  const requestOptions = typeof options === "string" ? { message: options } : options;
  return new Promise((resolve) => {
    queue = [...queue, { ...requestOptions, id: nextRequestId, resolve }];
    nextRequestId += 1;
    emitChange();
  });
}

function settleRequest(id: number, confirmed: boolean) {
  const request = queue.find((item) => item.id === id);
  if (!request) return;
  queue = queue.filter((item) => item.id !== id);
  request.resolve(confirmed);
  emitChange();
}

export function PlatformConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  busyLabel,
  danger = false,
  busy = false,
  locale = readLocale(),
  onConfirm,
  onCancel,
}: PlatformConfirmOptions & {
  open: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  if (!open) return null;
  return (
    <PlatformDialogSurface
      open={open}
      titleId={titleId}
      busy={busy}
      className="reset-dialog platform-confirm-dialog"
      initialFocusRef={cancelRef}
      onClose={onCancel}
    >
      <h2 id={titleId}>{title ?? (locale === "sq" ? "Konfirmo veprimin" : "Confirm action")}</h2>
      <p>{message}</p>
      <div className="drawer-actions">
        <button ref={cancelRef} type="button" className="secondary-action" disabled={busy} onClick={onCancel}>
          {cancelLabel ?? (locale === "sq" ? "Anulo" : "Cancel")}
        </button>
        <button
          type="button"
          className={danger ? "primary-action danger-action" : "primary-action"}
          disabled={busy}
          onClick={onConfirm}
        >
          {busy ? (busyLabel ?? (locale === "sq" ? "Duke punuar…" : "Working…")) : (confirmLabel ?? (locale === "sq" ? "Vazhdo" : "Continue"))}
        </button>
      </div>
    </PlatformDialogSurface>
  );
}

export function PlatformConfirmDialogHost() {
  const platformLocale = usePlatformLocale();
  const request = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  if (!request) return null;
  const locale = request.locale ?? platformLocale;
  return (
    <PlatformConfirmDialog
      open
      title={request.title ?? (request.danger ? (locale === "sq" ? "Konfirmo fshirjen" : "Confirm deletion") : undefined)}
      message={request.message}
      confirmLabel={request.confirmLabel ?? (request.danger ? (locale === "sq" ? "Fshi" : "Delete") : undefined)}
      cancelLabel={request.cancelLabel ?? (locale === "sq" ? "Anulo" : "Cancel")}
      locale={locale}
      danger={request.danger}
      onConfirm={() => settleRequest(request.id, true)}
      onCancel={() => settleRequest(request.id, false)}
    />
  );
}
